/**
 * PaymentFailedPage — F-010 (ritorno da Stripe Checkout, pagamento annullato/fallito).
 *
 * Route `/app/pratiche/:id/payment-failed`. La pratica è ancora BOZZA: l'utente
 * può **riprovare** il pagamento (ricrea una Checkout sulla stessa pratica, nessun
 * duplicato) o tornare alle proprie pratiche.
 */
import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { XCircle } from 'lucide-react';
import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { AppLayout } from '@/components/layout/AppLayout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { submit, isPaymentRequired } from '../lib/pratica-client';
import { buildConsentBody } from '../lib/consent';
import { resolveSubmitError } from '../lib/submit-error';

export function PaymentFailedPage() {
  return (
    <RequireAuth roles={['CLIENT']}>
      <AppLayout>
        <PaymentFailedInner />
      </AppLayout>
    </RequireAuth>
  );
}

export function PaymentFailedInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleRetry() {
    if (!id) return;
    setRetrying(true);
    setError(null);
    try {
      const result = await submit(id, await buildConsentBody());
      if (isPaymentRequired(result)) {
        window.location.href = result.checkoutUrl;
        return;
      }
      // Inatteso (es. già inviata): porta al dettaglio.
      navigate(`/app/pratiche/${id}`);
    } catch (err) {
      setError(resolveSubmitError(err).message);
      setRetrying(false);
    }
  }

  return (
    <section className="mx-auto max-w-lg space-y-6">
      <Card>
        <CardContent className="space-y-4 pt-6 text-center">
          <XCircle className="mx-auto h-12 w-12 text-destructive" aria-hidden="true" />
          <h1 className="text-2xl font-semibold">Pagamento non riuscito</h1>
          <p className="text-muted-foreground">
            Il pagamento è stato annullato o non è andato a buon fine. La pratica non è
            stata inviata: puoi riprovare quando vuoi, i dati sono salvati.
          </p>

          {error ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-center">
            <Button type="button" onClick={handleRetry} disabled={retrying}>
              {retrying ? 'Riapertura pagamento…' : 'Riprova il pagamento'}
            </Button>
            <Button asChild variant="outline">
              <Link to="/pratiche">Le mie pratiche</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
